import axios, { AxiosError } from "axios";
import { AsyncThunk } from "@reduxjs/toolkit";
import {
  EBasicAlertIcon,
  IBasicAlertProps,
} from "../../Framework/BasicAlert/BasicAlert";

export interface IAxiosError {
  message: string;
  name: string;
}

export interface IAxiosClientHelperArgs {
  result: IAxiosError;
  thunk: AsyncThunk<undefined, void, {}>;
  onClose: (event: unknown) => void;
}

//////////////////////////////////////////////////////////////////////////////////////
export const AxiosErrorHandlerX = (error: unknown): IAxiosError => {
  const err: IAxiosError = {
    message: "Error - ",
    name: "default"
  };

  if (error === undefined || error === null) {
    err.message = "Undefined error object.";
    return err;
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;

    if (axiosError.response) {
      const { data, status } = axiosError.response;

      // SQL / backend exception
      if ((data as any)?.ExceptionMessage) {
        err.message = String((data as any).ExceptionMessage).split("\n")[0];
        err.name = "SQL Error";
        return err;
      }

      if (status) {
        err.name = `${status} : HTTP Error`;

        switch (status) {
          case 401:
            err.message = "User name or password is incorrect.";
            break;

          case 404:
            err.message =
              "This url does not exist. " +
              (axiosError.request?.responseURL || "");
            break;

          case 500:
            err.message = "Internal server error";
            break;

          default:
            err.message = "Request failed";
        }

        return err;
      }
    }

    // Axios error without response (network, CORS, timeout)
    err.message = axiosError.message || "Network error";
    err.name = "Axios Error";
    return err;
  }

  // Non-Axios Error
  if (error instanceof Error) {
    err.message = error.message;
    err.name = error.name;
    return err;
  }

  err.message = "Unknown error occurred";
  err.name = "Unknown Error";
  return err;
};

export const AxiosErrorHandler = (error: any): IAxiosError => {
  const err: IAxiosError = { message: "Error - ", name: "default" };

  if (error === undefined) {
    err.message = "Undefined error object.";
    return err;
  }

  if (error.response) {
    //if a SQL error
    if (error.response.data.ExceptionMessage) {
      err.message = String(error.response.data.ExceptionMessage).split("\n")[0];
      err.name = "SQL Error";
    } else if (error.response.status) {
      //Handle 400, 500 etc errors
      err.name = error.response.status.toString() + " : HTTP Error";
      switch (error.response.status) {
        case 401:
          err.message = "User name or password is incorrect.";
          //dispatch(clearPassword());
          break;
        case 404:
          err.message =
            "This url does not exist. " + (error.request.responseURL || "");
          break;
        case 500:
          err.message = "Internal server error";
          break;
      }
    }
    return err;
  }

  //Network Errors
  if (error.toString().includes("Network Error")) {
    //generic error as a problem with the request
    err.message = "You are no longer connect to the network.";
    err.name = "Network Error";
    return err;
  }

  //Axios timeout
  if (error.code && error.code === "ECONNABORTED") {
    err.message =
      (error.message || "") +
      " axios error, is your URL wrong or DNS down or VPN not connected?";
    err.name = error.code + " : Axios Error";
    return err;
  }

  //stored proc typo?
  if (
    error.message &&
    error.message === "Cannot read properties of undefined (reading 'map')"
  ) {
    //Stored procedure doesnt exist or you have a typo, so the reflective API cannot map out the parameters
    err.message =
      (error.message || "") +
      " is you stored procedure name correct (typo? new version?)";
    err.name = "";
    return err;
  }

  return err;
};

///////////////////////////////////// AxiosClientHelper
/**
 *
 * @param error
 * @example
 * ```
 * const resultAction = await dispatch(nameOfYourThunk());
 *  let errorArgs = await AxiosClientHelper(
 *    resultAction.payload as IAxiosError,
 *     onClose //the callback run when the user presses the OK button, e.g. clear the dialog arguments so its hidden SetErrorArgs({ ...errorArgs, open: false });
 *   );
 *   SetErrorArgs(errorArgs); //local state, setting the errorArgs will show the dialog if errorArgs.open is true
 * ```
 */
export const AxiosClientHelper = async (
  payload: IAxiosError,
  callback: () => void
) => {
  //Default return settings
  let retval: IBasicAlertProps = {
    icon: EBasicAlertIcon.ERROR,
    title: "",
    message: "",
    onClose: () => {},
    open: false,
  };

  if(payload === undefined)
  {
    retval = {
      icon: EBasicAlertIcon.ERROR,
      title: "Error",
      message: "Undefined Payload",
      onClose: callback,
      open: true,
    };
    return retval;
  }

  if (payload.message === undefined) {
    //console.log("Good axios call:", payload);
  } else if (payload) {
    retval = {
      icon: EBasicAlertIcon.ERROR,
      title: payload.name,
      message: payload.message,
      onClose: callback,
      open: true,
    };
  }
  return retval;
};

export interface IAxiosError extends Error {}
